import { useEffect, useRef } from 'preact/hooks'
import { useSearchspringContext } from 'libs/searchspring/fetchers/provider'
import { useIntersectionObserver } from '@/components/hooks/useIntersectionObserver'
import { usePageTracker } from 'libs/searchspring/hooks/usePageTracker'

function ProductCountSummary() {
  const { results } = useSearchspringContext()
  const { loadedPages } = usePageTracker()

  if (!loadedPages.length) return null

  const totalResults = results?.pagination?.totalResults ?? 0
  if (totalResults === 0) return null

  const perPage = results?.pagination?.perPage ?? (window.settings?.per_page || 12)
  const firstLoadedPage = loadedPages[0]
  const lastLoadedPage = loadedPages[loadedPages.length - 1]
  const loadedCount = Math.max(0, Math.min(lastLoadedPage * perPage, totalResults) - (firstLoadedPage - 1) * perPage)
  const productLabel = totalResults === 1 ? 'product' : 'products'

  return (
    <p className="paragraph-xs">{loadedCount.toLocaleString()} of {totalResults.toLocaleString()} {productLabel}</p>
  )
}

export function InfiniteScrollNext() {
  const { results, refine, isLoading } = useSearchspringContext()
  const { loadedPages } = usePageTracker()
  const totalPages = results?.pagination?.totalPages ?? 1
  const { setNode, entry } = useIntersectionObserver({
    rootMargin: '300px',
    threshold: 0.1
  })

  useEffect(() => {
    if (isLoading) return
    if (entry?.isIntersecting) {
      handleNextPage()
    }
  }, [entry])

  const handleNextPage = () => {
    const nextPage = loadedPages[loadedPages.length - 1] + 1
    if (nextPage > totalPages) return
    refine('page', nextPage)
  }

  // Hide load more if all pages are loaded
  if (loadedPages[loadedPages.length - 1] >= totalPages) return null
  return (
    <div className="col-span-full flex flex-col items-center justify-center gap-x-1 gap-y-0.5 py-1.5">
      <ProductCountSummary />
      <button ref={setNode} className="btn btn-primary btn-border btn-m w-fit" onClick={handleNextPage} aria-label="Load More">
        Load More
      </button>
    </div>
  )
}

export function InfiniteScrollPrevious() {
  const { refine, isLoading } = useSearchspringContext()
  const { loadedPages } = usePageTracker()
  const hasBeenClicked = useRef(false)
  const { setNode, entry } = useIntersectionObserver({
    rootMargin: '100px',
    threshold: 1
  })

  useEffect(() => {
    if (!hasBeenClicked.current) return
    if (isLoading) return
    if (entry?.isIntersecting) {
      handlePrevPage()
    }
  }, [entry])

  const handleClick = () => {
    hasBeenClicked.current = true
    handlePrevPage()
  }

  // Handle previous page based on array of loaded pages
  const handlePrevPage = () => loadedPages.length && loadedPages[0] !== 1 && refine('page', loadedPages[0] - 1)

  // Hide load previous if first page is loaded
  if (!loadedPages.length || loadedPages[0] === 1) return null

  return (
    <div className="col-span-full flex flex-col items-center justify-center gap-x-1 gap-y-0.5 py-1.5">
      <ProductCountSummary />
      <button ref={setNode} className="btn btn-primary btn-border btn-m w-fit" onClick={handleClick} aria-label="Load Previous">
        Load Previous
      </button>
    </div>
  )
}
